"use client";

import { Button } from "@/components/atoms";
import VariantSelector from "@/components/atoms/VariantSelector/VariantSelector";
import useLocation from "@/lib/hooks/useLocation";
import usePriceList from "@/lib/hooks/useSelection/cart/usePriceList";

import {
  eventProductDetail,
  generateGTagItem,
} from "@/lib/utils/Analytics/Functions";
import { useEffect, useRef, useState } from "react";

import { CustomisationModal } from "@/components/molecules/Engraving/CustomisationModal";
import { EngravingStatus } from "@/components/molecules/Engraving/EngravingStatus";
import { PatchStatus } from "@/components/molecules/Patch/PatchStatus";
import useModal from "@/lib/hooks/useModal";
import { CustomizationsStoryblok } from "@/types/storyblok-blok-types";
import { useTranslations } from "next-intl";
import useSizes from "../../hooks/useSizes";
import type { PDPProduct } from "../../page";
import ComingSoonModal from "../ComingSoonModal/ComingSoonModal";
import { useProductContext } from "../ProductContext";
import SizeSelector from "../SizeSelector/SizeSelector";
import styles from "./action-section.module.css";
import { useProductButtonLabel } from "./getButtonLabel";
import { useAddToCart } from "./useAddToCart";
export type Sizes = ReturnType<typeof useSizes>;

interface IActionSectionProps {
  product: PDPProduct;
  customisationContent: CustomizationsStoryblok;
}

const ActionSection = ({
  product: initialProduct,
  customisationContent,
}: IActionSectionProps) => {
  const { product, isLoading: productLoading } = useProductContext();
  const t = useTranslations();
  const { currency } = useLocation();
  const { toggleModal } = useModal("notify", () => {});
  const prices = usePriceList(product?.prices);
  const sizes = useSizes(product?.sizes);
  const [engraving, setEngraving] = useState("");
  const [patch, setPatch] = useState("");
  const eventFired = useRef(false);
  const buttonLabel = useProductButtonLabel({ sizes, product });
  const { addToCart, isLoading: cartLoading } = useAddToCart({
    sizes,
    product,
    setModalOpen: toggleModal,
    prices,
    engraving,
    patch,
  });

  useEffect(() => {
    if (!eventProductDetail || !prices?.priceAsNumber) {
      return;
    }
    if (!eventFired.current) {
      eventProductDetail({
        currency: currency || "",
        items: [generateGTagItem(product, prices?.priceAsNumber)],
      });
      eventFired.current = true;
    }
  }, [product, currency, prices?.priceAsNumber]);
  return (
    <div>
      <VariantSelector isLoading={productLoading} product={product} />
      <SizeSelector sizes={sizes} />

      <ComingSoonModal item={sizes.selectedSize} product={product} />

      <CustomisationModal
        setEngraving={product?.engravingEnabled ? setEngraving : undefined}
        setPatch={product?.patchEnabled ? setPatch : undefined}
        content={customisationContent}
      />

      <div className={styles["product-actions"]}>
        <EngravingStatus
          engraving={engraving}
          removeEngraving={() => setEngraving("")}
          className={styles.customizationStatus}
        />
        <PatchStatus
          letter={patch}
          removePatch={() => setPatch("")}
          className={styles.customizationStatus}
        />

        {/* There is no category in product item */}
        {(product?.engravingEnabled || product?.patchEnabled) && (
          <Button modal="customisation" outlined>
            {engraving || patch
              ? t("product.engraving.button.edit")
              : t("product.engraving.button.add")}
          </Button>
        )}

        <Button
          onClick={addToCart}
          disabled={buttonLabel?.disabled}
          className="uppercase"
          isLoading={cartLoading}
        >
          {buttonLabel?.label}
        </Button>
      </div>

      <div className={styles.disclaimers}>
        <span>{t("product.disclaimers")}</span>
      </div>
    </div>
  );
};

export default ActionSection;
